import { GeofenceLocation } from "@verity/proctoring-option/types";
import {
	CoreButton,
	CoreErrorMessage,
	CoreIconButton,
	CoreInfoIndicator,
	CoreMenuItem,
	CoreModal,
	CoreModalDescription,
	CoreNoResults,
	CoreSelect,
	CoreSlider,
	CoreTextField,
	coreToast,
} from "../../../core";
import { LocateControl } from "leaflet.locatecontrol";
import "leaflet.locatecontrol/dist/L.Control.Locate.min.css";
import "leaflet/dist/leaflet.css";
import L, { Map, Circle, LatLngExpression } from "leaflet";
import i18nPackage from "../../../i18n/i18n";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Button, ListItemText, Skeleton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import Trash from "../../images/trash-simple.svg?react";
import styles from "./geofence-modal.module.css";
import { useMobileLayout } from "../../../hooks";
import { v4 as uuidv4 } from "uuid";
import MapPin from "../../images/map-pin.svg";
import {
	convertMetersToFeet,
	convertFeetToMeters,
	convertGeolocationPositionErrorToFailReason,
} from "../../../before-quiz/steps/system-check";
import Arrow from "../../images/arrow-filled.svg?react";
import { Trans } from "react-i18next";

const MAX_GEOFENCES = 5;
const DEFAULT_RADIUS_M = 10;
const DEFAULT_LNG = 0;
const DEFAULT_LAT = 0;

function getMinRadius(isMetric: boolean) {
	const minRadiusMeters = 10;
	return isMetric ? minRadiusMeters : convertMetersToFeet(minRadiusMeters);
}

function getMaxRadius(isMetric: boolean) {
	const maxRadiusMeters = 10000;
	return isMetric ? maxRadiusMeters : convertMetersToFeet(maxRadiusMeters);
}

function GeofenceLocationViewOnly({
	onClick,
	geofence,
	isSelected,
}: {
	onClick: () => void;
	geofence: GeofenceLocationWithId;
	isSelected: boolean;
}) {
	return (
		<Button
			onClick={onClick}
			className={`${styles.geofenceLocation} ${isSelected ? styles.selected : ""}`}
			style={{ gridColumn: 1 }}
			endIcon={isSelected ? <Arrow fill={"#42296E"} /> : undefined}
		>
			<Typography>{geofence.name}</Typography>
		</Button>
	);
}

function GeofenceLocationEditable({
	updateGeofences,
	geofence,
	onTextFieldClick,
	onDelete,
	getGeofenceError,
}: {
	updateGeofences: (name: string, error?: GeofenceError) => void;
	geofence: GeofenceLocationWithId;
	onTextFieldClick: () => void;
	onDelete: () => void;
	getGeofenceError: (geofence: GeofenceLocationWithId) => GeofenceError | undefined;
}) {
	const [name, setName] = useState<string>(geofence.name);
	const [error, setError] = useState<GeofenceError | undefined>(geofence.error);
	const errorMessage = error ? getErrorMessage(error) : undefined;
	return (
		<>
			<CoreTextField
				onChange={(e) => {
					const newName = e.target.value;
					setName(newName);
					const newError = getGeofenceError({ ...geofence, name: newName });
					setError(newError);
					updateGeofences(newName, newError);
				}}
				value={name}
				alwaysShowLength={true}
				maxLength={50}
				className={styles.geofenceLocationTextfield}
				size="small"
				onClick={onTextFieldClick}
			/>
			<CoreIconButton
				variant="plain"
				onClick={onDelete}
				tooltip={i18nPackage.t("editQuizPage.configurationStep.geofenceModal.edit.deleteAriaLabel", { location: name })}
				size="medium"
				className={styles.deleteButton}
			>
				<Trash />
			</CoreIconButton>
			{errorMessage && (
				<div className={styles.errorContainer}>
					<CoreErrorMessage>{errorMessage}</CoreErrorMessage>
				</div>
			)}
		</>
	);
}

function isValidRadius(radius: string | number, isMetric: boolean) {
	const minRadius = getMinRadius(isMetric);
	const maxRadius = getMaxRadius(isMetric);

	if (typeof radius === "number") return radius >= minRadius && radius <= maxRadius;
	if (!radius.trim()) return false;

	const numRadius = Number(radius);
	if (!Number.isFinite(numRadius)) return false;

	return numRadius >= minRadius && numRadius <= maxRadius;
}

function RadiusSlider(props: { onChange: (radius: number) => void; radius: number; isMetric: boolean }) {
	const { onChange, isMetric } = props;
	const sliderId = useId();

	// For typing so we dont update parent on every key down
	const [radiusValue, setRadiusValue] = useState<string>(String(props.radius));

	const minRadius = getMinRadius(isMetric);
	const maxRadius = getMaxRadius(isMetric);

	const metricLabel = i18nPackage.t("editQuizPage.configurationStep.geofenceModal.metricUnit");
	const imperialLabel = i18nPackage.t("editQuizPage.configurationStep.geofenceModal.imperialUnit");
	const unitLabel = isMetric ? metricLabel : imperialLabel;

	// If value ever changes, sync state
	useEffect(() => {
		setRadiusValue(String(props.radius));
	}, [props.radius]);

	return (
		<Stack spacing={0.5}>
			<Typography id={sliderId}>
				{i18nPackage.t("editQuizPage.configurationStep.geofenceModal.radius")}
				{": "}
				{i18nPackage.t("editQuizPage.configurationStep.geofenceModal.edit.radiusLabel", {
					minRadius: minRadius.toLocaleString(),
					maxRadius: maxRadius.toLocaleString(),
					unit: unitLabel,
				})}
			</Typography>
			<Stack direction={"row"} spacing={1} alignItems={"center"}>
				<CoreSlider
					onChange={(value) => {
						onChange(value);
						setRadiusValue(String(value));
					}}
					value={props.radius}
					thumbShape="rectangle"
					ariaLabelledBy={sliderId}
					min={minRadius}
					max={maxRadius}
				/>
				<CoreTextField
					onChange={(e) => {
						const value = e.target.value;
						setRadiusValue(value);
						if (isValidRadius(value, isMetric)) {
							onChange(Number(value));
						}
					}}
					onBlur={(e) => {
						if (!isValidRadius(e.target.value, isMetric)) {
							onChange(minRadius);
							setRadiusValue(String(minRadius));
							coreToast.dismiss();
							coreToast.error(
								i18nPackage.t("editQuizPage.configurationStep.geofenceModal.error.invalidRadius", {
									minRadius: minRadius.toLocaleString(),
									maxRadius: maxRadius.toLocaleString(),
									unit: unitLabel,
								}),
							);
						}
					}}
					value={radiusValue}
					size="small"
					sx={{ width: "250px" }}
					aria-labelledby={sliderId}
					inputProps={{
						sx: {
							textAlign: "center",
						},
					}}
				/>
				<Typography minWidth={"50px"} maxWidth={"50px"}>
					{unitLabel}
				</Typography>
			</Stack>
		</Stack>
	);
}

function getGeofenceError(
	geofence: GeofenceLocationWithId,
	geofences: GeofenceLocationWithId[],
): GeofenceError | undefined {
	if (!geofence.name.trim()) return "empty";

	const geofencesFiltered = geofences.filter((g) => g.id !== geofence.id);

	const duplicateName = geofencesFiltered.some((g) => g.name === geofence.name);

	return duplicateName ? "duplicate" : undefined;
}

function getErrorMessage(error: GeofenceError) {
	const errorKey = error === "duplicate" ? "duplicateName" : "emptyName";
	return i18nPackage.t(`editQuizPage.configurationStep.geofenceModal.error.${errorKey}`);
}

function GeofenceMap({
	geofence,
	onUpdate,
}: {
	geofence: GeofenceLocation;
	onUpdate: (longitude: number, latitude: number) => void;
}) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const mapRef = useRef<Map | null>(null);
	const circleRef = useRef<Circle | null>(null);
	const markerRef = useRef<L.Marker | null>(null); // Optional: for center point

	const INITIAL_CENTER: LatLngExpression = [geofence.latitude, geofence.longitude];
	const INITIAL_ZOOM = 1;

	useEffect(() => {
		// 1. Create the map instance
		if (!containerRef.current || mapRef.current) return;

		const map = L.map(containerRef.current).setView(INITIAL_CENTER, INITIAL_ZOOM);
		mapRef.current = map;

		// 2. Add the Tile Layer (Map visuals)
		L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
			maxZoom: 19,
			attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
		}).addTo(map);

		// 3. Add the circle -> conver to meters if in feet
		const initialCircle = L.circle(INITIAL_CENTER, {
			radius: geofence.isMetric ? geofence.radius : convertFeetToMeters(geofence.radius),
			color: "rgb(66, 41, 110)",
		}).addTo(map);
		circleRef.current = initialCircle;

		// 4. Add initial Marker
		const initialMarker = L.marker(INITIAL_CENTER, {
			icon: L.icon({ iconUrl: MapPin, iconSize: [64, 64], iconAnchor: [32, 64] }),
		}).addTo(map);
		markerRef.current = initialMarker;

		new LocateControl({
			flyTo: false,
			setView: "untilPan",
		}).addTo(map);

		map.on("click", (e: L.LeafletMouseEvent) => {
			const { lat, lng } = e.latlng;
			onUpdate(lng, lat);
		});

		map.on("locationerror", (e) => {
			const error = convertGeolocationPositionErrorToFailReason(e);
			const errorKey = error === "notInRadius" || error === "timeout" ? "unknown" : "noPermission";
			coreToast.error(i18nPackage.t(`editQuizPage.configurationStep.geofenceModal.error.${errorKey}`));
		});

		map.whenReady(() => {
			setTimeout(() => {
				if (geofence.longitude === DEFAULT_LNG || geofence.latitude === DEFAULT_LAT) {
					map.setZoom(3);
				}
			}, 100);
		});

		// 5. Cleanup function: removes the map instance when the component unmounts
		return () => {
			map.remove();
			mapRef.current = null;
		};
	}, []);

	// Updates circle
	useEffect(() => {
		const map = mapRef.current;
		const circle = circleRef.current;
		const marker = markerRef.current;

		if (map && circle && marker) {
			const newCenter: LatLngExpression = [geofence.latitude, geofence.longitude];

			// 1. Update the Circle's geometry
			circle.setLatLng(newCenter);

			circle.setRadius(geofence.isMetric ? geofence.radius : convertFeetToMeters(geofence.radius));

			// 2. Update the Marker's position
			marker.setLatLng(newCenter);

			// Move map to circle and make sure circle fits in map
			map.flyTo(newCenter, map.getZoom(), { duration: 0.5 });
			map.fitBounds(circle.getBounds(), { padding: [50, 50], duration: 0.8 });
		}
	}, [geofence]);

	return <div id="map" className={styles.geofenceMap} ref={containerRef} />;
}

function getDefaultGeofence(number?: number, userLocation?: GeofenceLocation): GeofenceLocationWithId {
	const defaultName = number
		? i18nPackage.t("editQuizPage.configurationStep.geofenceModal.edit.location", {
				number: number,
			})
		: i18nPackage.t("editQuizPage.configurationStep.geofenceModal.view.manualLocation");

	const radius = userLocation?.radius
		? userLocation.radius
		: userLocation?.isMetric
			? DEFAULT_RADIUS_M
			: convertMetersToFeet(DEFAULT_RADIUS_M);

	return {
		latitude: userLocation?.latitude ?? DEFAULT_LAT,
		longitude: userLocation?.longitude ?? DEFAULT_LNG,
		radius: radius,
		name: defaultName,
		id: uuidv4(),
		isMetric: userLocation?.isMetric ?? !isUserInUS(),
	};
}

// Also adds ids
function getInitialGeofences(
	selectedGeofence: GeofenceLocation | undefined,
	presetGeofences: GeofenceLocation[] | undefined,
	allowEdit: boolean,
	userGeoLocation?: GeofenceLocation,
): { initialSelectedGeofenceId: string | undefined; initialGeofences: GeofenceLocationWithId[] } {
	// Helper to add ID if it doesn't exist
	const addId = (g: GeofenceLocation): GeofenceLocationWithId => ({
		...g,
		id: (g as GeofenceLocationWithId).id ?? uuidv4(),
	});

	const manualGeofence = getDefaultGeofence(undefined, userGeoLocation);
	const nonNullpresetGeofences = presetGeofences ? presetGeofences : [];
	const hasManualGeofence = nonNullpresetGeofences.some(
		(g) => g.name === i18nPackage.t("editQuizPage.configurationStep.geofenceModal.view.manualLocation"),
	);
	// If viewing, add default to the start of the array
	if (!allowEdit && !hasManualGeofence) nonNullpresetGeofences.unshift(manualGeofence);

	const initialGeofences = nonNullpresetGeofences.map(addId);

	// 1. Use selected geofence
	// 2. Use the manual label if not editing
	// 3. If editing and no savedgeofences -> undefined
	// Also add Id since name cannot be used as a key
	const initialSelectedGeofenceId: string | undefined = selectedGeofence
		? addId(selectedGeofence).id
		: initialGeofences.length > 0
			? initialGeofences[0].id
			: undefined;

	return { initialSelectedGeofenceId, initialGeofences };
}

function isUserInUS() {
	try {
		const zone = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;

		if (!zone) return false;

		// Common US-specific timezones
		const usZones = [
			"America/New_York",
			"America/Chicago",
			"America/Denver",
			"America/Los_Angeles",
			"America/Phoenix",
			"America/Anchorage",
			"America/Honolulu",
		];

		return usZones.includes(zone);
	} catch (e) {
		// Fallback to locale if Intl fails
		return navigator.language === "en-US";
	}
}

function GeolocationSkeleton() {
	return (
		<Stack spacing={1} width={"100%"}>
			<Skeleton variant="rectangular" width={"100%"} />
			<Stack direction={"row"} width={"100%"} spacing={3}>
				<Skeleton variant="rectangular" width={"350px"} height={510} />
				<Stack spacing={1} width={"450px"}>
					<Skeleton variant="rectangular" width={"100%"} height={300} />
					<Skeleton variant="rectangular" width={"100%"} height={200} />
				</Stack>
			</Stack>
		</Stack>
	);
}

type GeofenceError = "duplicate" | "empty";

type GeofenceLocationWithId = GeofenceLocation & {
	id: string;
	error?: GeofenceError;
};

type GeofenceModalProps = {
	onClose: () => void;
	allowEdit: boolean;
	selectedGeofence?: GeofenceLocation;
	presetGeofences?: GeofenceLocation[];
	onSave?: (selectedGeofence: GeofenceLocation | undefined, geofences: GeofenceLocation[]) => void;
	isLoading?: boolean;
};

export default function GeofenceModal(props: GeofenceModalProps) {
	const { allowEdit, onClose, onSave } = props;
	const isMobileLayout = useMobileLayout();
	const theme = useTheme();
	const isMediumBreakpoint = useMediaQuery(theme.breakpoints.down("md"), {
		matchMedia: (query) => {
			return window.matchMedia(query);
		},
	});
	const [defaultNumber, setDefaultNumber] = useState<number>(0);
	const [isFetching, setIsFetching] = useState<boolean>(false);
	const [userGeoLocation, setUserGeoLocation] = useState<GeofenceLocation | undefined>(undefined);

	// Keeping track of Id so I don't have to update 2 states
	const [selectedGeofenceId, setSelectedGeofenceId] = useState<string | undefined>(undefined);
	const [geofences, setGeofences] = useState<GeofenceLocationWithId[]>([]);

	// Try to find geofence using the id. If unable to find (ex: user using saved geofence, but saved geofence got deleted), use first geofence
	const selectedGeofence = useMemo(() => {
		const found = geofences.find((g) => g.id === selectedGeofenceId);
		return found ?? geofences[0] ?? undefined;
	}, [geofences, selectedGeofenceId]);

	const translationKey = allowEdit ? "edit" : "view";

	// Try to get user's location
	useEffect(() => {
		const getPosition = (): Promise<GeolocationPosition> => {
			return new Promise((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(resolve, reject, {
					timeout: 3000,
					maximumAge: 0,
					enableHighAccuracy: false,
				});
			});
		};

		const getUserLocation = async () => {
			try {
				setIsFetching(true);
				const { accuracy, latitude, longitude } = (await getPosition()).coords;
				const isMetric = !isUserInUS();
				const roundedRadius = Number(accuracy.toFixed(0));
				const radius = isMetric ? roundedRadius : convertMetersToFeet(roundedRadius);

				setUserGeoLocation({
					isMetric,
					latitude,
					longitude,
					radius,
					name: "",
				});
			} catch (error) {
				setUserGeoLocation(undefined);
			} finally {
				setIsFetching(false);
			}
		};

		void getUserLocation();
	}, []);

	useEffect(() => {
		if (isFetching) return;
		const { initialGeofences, initialSelectedGeofenceId } = getInitialGeofences(
			props.selectedGeofence,
			props.presetGeofences,
			allowEdit,
			userGeoLocation,
		);
		setSelectedGeofenceId(initialSelectedGeofenceId);
		setGeofences(initialGeofences);
	}, [props.selectedGeofence, props.presetGeofences, allowEdit, userGeoLocation, isFetching]);

	function handleAddLocation() {
		if (geofences.length >= MAX_GEOFENCES) return;
		const newDefaultNumber = defaultNumber + 1;
		const newGeofence = getDefaultGeofence(newDefaultNumber, userGeoLocation);
		setGeofences((prev) => [...prev, newGeofence]);
		setSelectedGeofenceId(newGeofence.id);
		setDefaultNumber(newDefaultNumber);
	}

	function handleDeleteLocation(geofence: GeofenceLocationWithId) {
		const newGeofences: GeofenceLocationWithId[] = geofences.filter((g) => g.id !== geofence.id);
		setGeofences(newGeofences);
		setSelectedGeofenceId(newGeofences.length > 0 ? newGeofences[0].id : undefined); // Select first geofence (copied from engage)
	}

	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18nPackage.t(`editQuizPage.configurationStep.geofenceModal.${translationKey}.title`)}
			buttons={
				<Stack direction={"row"} justifyContent={"flex-end"} spacing={1}>
					<CoreButton onClick={onClose} color="secondary">
						{i18nPackage.t("editQuizPage.configurationStep.geofenceModal.cancel")}
					</CoreButton>
					<CoreButton
						onClick={() => onSave?.(selectedGeofence as GeofenceLocation, geofences as GeofenceLocation[])}
						disabled={allowEdit ? geofences.some((g) => g.error) : false}
					>
						{i18nPackage.t("editQuizPage.configurationStep.geofenceModal.save")}
					</CoreButton>
				</Stack>
			}
			minWidth={isMobileLayout || isMediumBreakpoint ? "unset" : "800px"}
			widthFixed={!isMobileLayout}
		>
			{props.isLoading && isFetching && <GeolocationSkeleton />}
			{!props.isLoading && !isFetching && (
				<>
					<CoreModalDescription>
						{i18nPackage.t(`editQuizPage.configurationStep.geofenceModal.${translationKey}.description`)}
					</CoreModalDescription>
					<Stack direction={isMobileLayout ? "column" : "row"} spacing={1} marginTop={"20px"}>
						<div className={styles.presetGeofenceGrid}>
							{allowEdit ? (
								<CoreButton onClick={handleAddLocation} disabled={geofences.length >= MAX_GEOFENCES}>
									{i18nPackage.t("editQuizPage.configurationStep.geofenceModal.edit.addLocation")}
								</CoreButton>
							) : (
								<Stack direction={"row"} spacing={1}>
									<Typography color={"#000"}>
										{i18nPackage.t("editQuizPage.configurationStep.geofenceModal.view.savedGeofence")}
									</Typography>
									{!allowEdit && geofences.length === 1 && (
										<CoreInfoIndicator
											hint={
												<Trans
													i18nKey="editQuizPage.configurationStep.geofenceModal.view.createLocationHint"
													components={{ b: <strong /> }}
												/>
											}
											size={20}
										/>
									)}
								</Stack>
							)}
							{geofences.map((geofence) =>
								allowEdit ? (
									<GeofenceLocationEditable
										key={geofence.id}
										geofence={geofence}
										onTextFieldClick={() => setSelectedGeofenceId(geofence.id)}
										onDelete={() => handleDeleteLocation(geofence)}
										updateGeofences={(name, error) =>
											setGeofences((prev) => prev.map((g) => (g.id === geofence.id ? { ...g, name, error: error } : g)))
										}
										getGeofenceError={(newGeofence) => getGeofenceError(newGeofence, geofences)}
									/>
								) : (
									<GeofenceLocationViewOnly
										key={geofence.id}
										geofence={geofence}
										onClick={() => setSelectedGeofenceId(geofence.id)}
										isSelected={selectedGeofence ? geofence.id === selectedGeofence.id : false}
									/>
								),
							)}
						</div>
						<Stack minWidth={isMobileLayout || isMediumBreakpoint ? "unset" : "450px"} width={"100%"} spacing={1}>
							{selectedGeofence ? (
								<>
									<Stack
										direction={isMobileLayout ? "column" : "row"}
										justifyContent={isMobileLayout ? "center" : "space-between"}
										alignItems={"center"}
										spacing={{ xs: 1, md: 5 }}
										width={"100%"}
									>
										<Typography className={styles.geofenceName}>{selectedGeofence.name}</Typography>
										<CoreSelect<string>
											key={selectedGeofence.id}
											renderValue={(value) => {
												const translationKey = value === "metricUnit" ? "metricUnit" : "imperialUnit"; // To satisfy typescript
												return (
													<span>{i18nPackage.t(`editQuizPage.configurationStep.geofenceModal.${translationKey}`)}</span>
												);
											}}
											value={selectedGeofence.isMetric ? "metricUnit" : "imperialUnit"}
											onChange={(e) => {
												const value = e.target.value;
												const isMetric = value === "metricUnit";
												const newRadius = isMetric
													? convertFeetToMeters(selectedGeofence.radius)
													: convertMetersToFeet(selectedGeofence.radius);
												setGeofences((prev) =>
													prev.map((g) =>
														g.id === selectedGeofence.id ? { ...g, isMetric: isMetric, radius: newRadius } : g,
													),
												);
											}}
											sx={{ width: "75px" }}
											label={i18nPackage.t("editQuizPage.configurationStep.geofenceModal.unitAriaLabel")}
										>
											<CoreMenuItem value="metricUnit">
												<ListItemText
													primary={i18nPackage.t("editQuizPage.configurationStep.geofenceModal.metricUnit")}
												/>
											</CoreMenuItem>
											<CoreMenuItem value="imperialUnit">
												<ListItemText
													primary={i18nPackage.t("editQuizPage.configurationStep.geofenceModal.imperialUnit")}
												/>
											</CoreMenuItem>
										</CoreSelect>
									</Stack>
									<GeofenceMap
										key={selectedGeofence.id}
										geofence={selectedGeofence}
										onUpdate={(lng, lat) => {
											setGeofences((prevGeofences) => {
												return prevGeofences.map((geofence) => {
													if (geofence.id === selectedGeofence.id) {
														return {
															...geofence,
															latitude: lat,
															longitude: lng,
														};
													}
													return geofence;
												});
											});
										}}
									/>
									<RadiusSlider
										radius={selectedGeofence.radius}
										isMetric={selectedGeofence.isMetric}
										onChange={(radius) => {
											setGeofences((prevGeofences) => {
												return prevGeofences.map((geofence) => {
													// Find selected geofence in existing array. If it exists update radius
													if (geofence.id === selectedGeofence.id) {
														return {
															...geofence,
															radius: radius,
														};
													}

													// Not selected geofence. Keep as is
													return geofence;
												});
											});
										}}
									/>
								</>
							) : (
								<CoreNoResults
									header={i18nPackage.t("editQuizPage.configurationStep.geofenceModal.noSavedLocationTitle")}
								>
									{i18nPackage.t("editQuizPage.configurationStep.geofenceModal.noSavedLocationDesc")}
								</CoreNoResults>
							)}
						</Stack>
					</Stack>
				</>
			)}
		</CoreModal>
	);
}
